import React from 'react';
import _ from 'lodash';
import { sirenActions, useSirenEntity } from '~/utility/BinSentry-ui-utility';
import { Button, Icon, makeStyles } from '~/lib/bn-material-ui';
import { DateTime } from 'luxon';
import { useStateValue } from './data/order-calculator-context';
import ContactMill from './contact-mill';
import { OrderSyncToFmmButton } from '../orders/components/order-sync-to-fmm-button';
import { OrderDownloadEaiXmlButton } from '../orders/integrations/eai/order-download-eai-xml-button';

export default function OrderButtons({ orderEntity, binEntity }) {
  const [{
    activeOrderId,
    activeOrder,
    editingOrder,
    ordersHref,
    isProducer,
  }, dispatch] = useStateValue();

  const { entity: ordersEntity } = useSirenEntity({ href: ordersHref });

  const isSuggestedOrder = orderEntity?.properties?.type === 'suggestion' && orderEntity?.properties?.state === 'ready';
  const isRequestedOrder = orderEntity?.properties?.type === 'request' && orderEntity?.properties?.state === 'ready';

  const originalValues = {
    deliveryDate: orderEntity?.properties.deliveryDate,
    quantity: orderEntity?.properties.quantity || 0,
    feedId: orderEntity?.getSubEntityByRel('https://api.binsentry.com/rel/feed')?.href,
    medicated: orderEntity?.properties.medicated || false,
  };

  const updatedValues = {
    deliveryDate: activeOrder.deliveryDate === undefined ? originalValues.deliveryDate : activeOrder.deliveryDate,
    quantity: activeOrder.orderQuantity === undefined ? originalValues.quantity : activeOrder.orderQuantity,
    feedId: activeOrder.feedId === undefined ? originalValues.feedId : activeOrder.feedId,
    medicated: activeOrder.medicated === undefined ? originalValues.medicated : activeOrder.medicated,
  };

  async function saveOrder({ action, values }) {
    values = values || updatedValues;
    const omittedValues = Object.keys(values).filter(property => !action.hasField(property));
    values = _.omit(values, omittedValues);
    const { entity: savedOrder } = await sirenActions.submitAction(action, values);
    dispatch({ type: 'saveOrder', orderEntity: savedOrder, binEntity });
  }

  const createOrder = () => saveOrder({ action: ordersEntity?.getAction('create-order') });
  const requestOrder = () => saveOrder({ action: ordersEntity?.getAction('request-order') });
  const updateOrder = (overrides = {}) => saveOrder({ action: orderEntity?.getAction('update-order'), values: { ...updatedValues, ...overrides } });
  const scheduleOrder = () => saveOrder({ action: orderEntity?.getAction('schedule-order'), values: { ...updatedValues } });

  async function closeOrder() {
    await sirenActions.submitAction(orderEntity.getAction('update-order'), { state: 'closed' });
    dispatch({ type: 'cancelOrder', binEntity });
  }

  const isPast = DateTime.fromISO(updatedValues.deliveryDate) < DateTime.now();

  const canSave = (() => {
    const isMissingRequiredFields = !updatedValues.feedId || !updatedValues.quantity;
    if (isPast || isMissingRequiredFields) {
      return false;
    }
    if (isSuggestedOrder || isRequestedOrder) {
      return true;
    }
    return !_.isEqual(updatedValues, originalValues);
  })();

  const canCreate = ordersEntity?.hasAction('create-order');
  const canRequest = ordersEntity?.hasAction('request-order');
  const canUpdate = orderEntity?.hasAction('update-order');
  const canSchedule = orderEntity?.hasAction('schedule-order');
  const canClose = orderEntity?.hasAction('close-order');

  if (isProducer) {
    if (!activeOrderId && !editingOrder) {
      return (isPast || !canRequest) ? null : <EditButton>Request Order</EditButton>;
    }

    if (editingOrder) {
      return <>
        <CancelEditButton/>
        {activeOrderId
          ? <SaveOrderButton disabled={!canSave} onClick={() => updateOrder()}/>
          : <SaveOrderButton disabled={!canSave || !canRequest} onClick={() => requestOrder()}>Send To Mill</SaveOrderButton>
        }
      </>;
    }

    if (canUpdate) {
      return <>
        <CloseOrderButton
          onConfirm={() => closeOrder()}
          confirmationText='Are you sure you want to cancel this request?'
        >
          Cancel Request
        </CloseOrderButton>
        <EditButton>Edit Request</EditButton>
      </>;
    }

    if (activeOrderId) {
      return <>
        <ContactMill/>
        <AcknowledgeOrder orderEntity={orderEntity}/>
      </>;
    }

    return null;
  }

  if (!activeOrderId && !editingOrder) {
    return <EditButton disabled={isPast || !canCreate}>Create Order</EditButton>;
  }

  if (editingOrder) {
    let cancel = <CancelEditButton/>;
    let save;
    let syncToFmm = null;

    if (isSuggestedOrder) {
      cancel = <CloseOrderButton
        onConfirm={() => closeOrder()}
        confirmationText='Are you sure you want to dismiss this suggestion?'
      >
        Dismiss Suggestion
      </CloseOrderButton>;
      save = <SaveOrderButton disabled={!canSave || !canSchedule} onClick={() => scheduleOrder()}>Confirm Order</SaveOrderButton>;
    } else if (isRequestedOrder) {
      cancel = <CloseOrderButton
        disabled={!canClose}
        onConfirm={() => closeOrder()}
        confirmationText='Are you sure you want to reject this request?'
      >
        Reject Request
      </CloseOrderButton>;
      save = <SaveOrderButton disabled={!canSave || !canSchedule} onClick={() => scheduleOrder()}>Confirm Order</SaveOrderButton>;
    } else if (activeOrderId) {
      const saveDisabled = !canSave || !canUpdate;
      save = <SaveOrderButton disabled={saveDisabled} onClick={() => updateOrder()}>Save Order</SaveOrderButton>;
      syncToFmm = <OrderSyncToFmmButton orderHref={activeOrderId} />;
    } else {
      save = <SaveOrderButton disabled={!canSave || !canCreate} onClick={() => createOrder()}>Create Order</SaveOrderButton>;
    }

    return <>{cancel}{syncToFmm}{save}</>;
  }

  if (activeOrderId) {
    return <>
      <CloseOrderButton
        disabled={!canClose}
        onConfirm={() => closeOrder()}
      />
      <OrderSyncToFmmButton orderHref={activeOrderId} />
      <OrderDownloadEaiXmlButton orderHref={activeOrderId} />
      <EditButton>Edit Order</EditButton>
    </>;
  }
}

const useStyles = makeStyles((theme) => {
  return {
    acknowledgement: {
      margin: theme.spacing(1),
      display: 'flex',
      alignItems: 'baseline',
      color: theme.palette.green[30],
      '& > .MuiIcon-root': {
        fontSize: '1em',
        marginRight: '.5em',
      },
    },
  };
});

function AcknowledgeOrder({ orderEntity }) {
  const [, dispatch] = useStateValue();
  const styles = useStyles();

  if (!orderEntity) {
    return null;
  }

  if (orderEntity.properties.acknowledgement === 'approved') {
    return <div className={styles.acknowledgement}>
      <Icon className='fas fa-check-circle' />
      Acknowledged
    </div>;
  }

  const canApprove = orderEntity.hasAction('approve-order');

  return !canApprove ? null : <Button
    variant='contained'
    startIcon={<Icon className='fas fa-check' />}
    onClick={async () => {
      const approveAction = orderEntity.getAction('approve-order');
      const { entity: updatedOrder } = await sirenActions.submitAction(approveAction);
      dispatch({ type: 'saveOrder', orderEntity: updatedOrder });
    }}
  >
    Acknowledge
  </Button>;
}

function EditButton({ children, disabled }) {
  const [, dispatch] = useStateValue();
  return <Button
    variant='contained'
    disabled={disabled}
    onClick={() => dispatch({ type: 'editOrder' })}
  >
    {children || 'Edit Order'}
  </Button>;
}

function CancelEditButton({ children }) {
  const [, dispatch] = useStateValue();
  return <Button
    variant='text'
    onClick={() => dispatch({ type: 'cancelOrderEditing' })}
  >
    {children || 'Cancel'}
  </Button>;
}

function CloseOrderButton({ children, disabled, confirmationText, onConfirm }) {
  const confirm = () => window.confirm(confirmationText || 'Are you sure you want to close this order?') && onConfirm();
  return <Button
    color='error'
    variant='text'
    disabled={disabled}
    onClick={confirm}
  >
    {children || 'Cancel Order'}
  </Button>;
}

function SaveOrderButton({ children, disabled, onClick }) {
  return <Button
    variant='contained'
    disabled={disabled}
    onClick={onClick}
  >
    {children || 'Save Order'}
  </Button>;
}
